// use this Api as a function to make api calls;
import axiosInstance from '../axios';
//import axios, { AxiosInstance } from 'axios';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { RolesLowerCase } from '../../enums/ROLES';
import { AxiosCacheInstance, CacheRequestConfig } from 'axios-cache-interceptor';

//import type { AxiosCacheInstance, CacheAxiosResponse }
// import type { AxiosCacheInstance };

export const loginWithEmailAndPassword = async (data: {
  email: string;
  password: string;
  rememberMe?: boolean;
  selectedOrgId?: string;
  asSuperAdmin?: boolean;
}) => {
  const { email, password, selectedOrgId: organization_id, asSuperAdmin } = data;
  if (organization_id) {
    const res = await axiosInstance.post(`${API_ROUTES.LOGIN}`, {
      email: email.toLocaleLowerCase(),
      password,
      organization_id,
    });
    return res;
  } else if (asSuperAdmin) {
    const res = await axiosInstance.post(`${API_ROUTES.LOGIN}`, {
      email: email.toLocaleLowerCase(),
      password,
      asSuperAdmin,
    });
    return res;
  }
  const res = await axiosInstance.post(`${API_ROUTES.LOGIN}`, {
    email: email.toLocaleLowerCase(),
    password,
  });
  return res;
};

export const logout = async () => {
  // const axiosCache = Api as AxiosCacheInstance;
  // if (axiosCache?.storage) {
  //   // @ts-ignore
  //   axiosCache.storage.clear();
  // }

  return await axiosInstance.get(API_ROUTES.LOGOUT);
};

export const selfRegister = async (data: { password: string; password_repeat: string }, invitation_token: string | null) => {
  const res = await axiosInstance.patch(`${API_ROUTES.REGISTER}?invitation_token=${invitation_token}`, data);
  return res;
};

export const forgotPassword = async (data: { email: string }) => {
  const res = await axiosInstance.post(`${API_ROUTES.FORGOT_PASSWORD}`, data);
  return res;
};

export const restorePassword = async (data: { password: string; password_repeat: string; forgot_password_token: string | null }) => {
  const res = await axiosInstance.post(`${API_ROUTES.RESTORE_PASSWORD}?forgot_password_token=${data.forgot_password_token}`, data);
  return res;
};

export const getMyUserInfo = async (forceRefresh = false) => {
  const config: CacheRequestConfig | undefined = forceRefresh ? { cache: false } : undefined;
  const res = await axiosInstance.get(`${API_ROUTES.ME}`, config);
  return res;
};

export const getAllRoles = async () => {
  const res = await axiosInstance.get(`${API_ROUTES.ROLES}`);
  return res.data;
};

export const getUsersByRolesArray = async (roles: RolesLowerCase[], search = '', ignoreUsers: string[] = [], page = 1, limit = 1000) => {
  const ignoreUsersQuery = `&ignoreUsers=${ignoreUsers.join('&ignoreUsers=')}`;
  const res = await axiosInstance.get(
    `${API_ROUTES.ORGANIZATION_USERS}?page=${page}&limit=${limit}&role=${roles.join('&role=')}&search=${search}${
      ignoreUsers.length > 0 ? ignoreUsersQuery : ''
    }`,
  );
  return res.data;
};

export const getExcludeList = async (search = '', ignoreUsers: string[] = [], ignoreGroups: string[] = []) => {
  const ignoreUsersQuery = `&ignoreUsers=${ignoreUsers.join('&ignoreUsers=')}`;
  const ignoreGroupsQuery = `&ignoreGroups=${ignoreGroups.join('&ignoreGroups=')}`;
  const res = await axiosInstance.get(
    `${API_ROUTES.ORGANIZATION_EXCLUDE_LIST}?search=${search}${ignoreUsers.length > 0 ? ignoreUsersQuery : ''}${
      ignoreGroups.length > 0 ? ignoreGroupsQuery : ''
    }`,
  );

  return res.data;
};

export const userUpdate = async (data: {
  username?: string;
  oldPassword?: string;
  password?: string;
  password_repeat?: string;
  file?: Blob;
}) => {
  const res = await axiosInstance.patch(API_ROUTES.ME, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return res;
};

export const deleteUserAvatar = async () => {
  const res = await axiosInstance.patch(API_ROUTES.DELETE_PROFILE_AVATAR);
  return res;
};

export const deleteUser = async (userId: string, organizationId: string) => {
  const res = await axiosInstance.patch(`${API_ROUTES.ORGANIZATION_REMOVE_USER}/${organizationId}`, {
    ids: [
      {
        id: userId,
      },
    ],
  });
  return res;
};

export const deleteUserFully = async (userId: string, orgId: string) => {
  const res = await axiosInstance.patch(`${API_ROUTES.REMOVE_USER_FULLY}/${orgId}`, {
    ids: [
      {
        id: userId,
      },
    ],
  });
  return res;
};

export type TdeleteUserAndTransferContent = {
  ids: {
    id: string;
  }[];
  substitution?: { userId: string; folderIds: string[] | 'all'; composerIds?: string[] }[];
};
export const deleteUserAndTransferContent = async (body: TdeleteUserAndTransferContent, organizationId: string) => {
  return await axiosInstance.patch(`${API_ROUTES.ORGANIZATION_REMOVE_USER}/${organizationId}`, body);
};

export const getDeleteUserSourceImpact = async (body: TdeleteUserAndTransferContent, organizationId: string) => {
  return await axiosInstance.post(`${API_ROUTES.ORGANIZATION_REMOVE_USER_DELETE_IMPACT}/${organizationId}/delete-impact`, body);
};

export const updateUserByAdmin = async (data: {
  email?: string;
  username?: string;
  password?: string;
  role_id?: string;
  userId: string;
  organization_id?: string;
  activate_remotely?: boolean;
  allow_download_own_composers?: boolean;
}) => {
  const res = await axiosInstance.patch(`${API_ROUTES.USER}/${data.userId}`, {
    ...data,
  });
  return res;
};

export const patchBulkDownloadPreferencesByRole = async (data: {
  role: 'Analyst' | 'Viewer';
  allow_download_own_composers: boolean;
}) => {
  return await axiosInstance.patch(
    API_ROUTES.USER_BULK_DOWNLOAD_PREFERENCES,
    data,
  );
};

export const getUserSettingsByPlatform = async (platform: 'web' | 'mobile') => {
  const res = await axiosInstance.get(`${API_ROUTES.USER_SETTINGS}?platform=${platform}`);
  return res.data;
};

export const patchUserSettingsByPlatform = async (
  platform: 'web' | 'mobile',
  data: Record<string, any>,
) => {
  const res = await axiosInstance.patch(`${API_ROUTES.USER_SETTINGS}?platform=${platform}`, data);
  return res.data;
};

export type EmailNotificationSettings = {
  shared_content_email_notifications_enabled: boolean;
  moved_content_email_notifications_enabled: boolean;
};

export const getEmailNotificationSettings = async () => {
  const res = await axiosInstance.get<EmailNotificationSettings>(
    API_ROUTES.USER_NOTIFICATION_SETTINGS,
  );
  return res.data;
};

export const patchEmailNotificationSettings = async (
  data: EmailNotificationSettings,
) => {
  const res = await axiosInstance.patch<EmailNotificationSettings>(
    API_ROUTES.USER_NOTIFICATION_SETTINGS,
    data,
  );
  return res.data;
};

export const applyUserSettingsByScope = async (data: {
  scope: 'org' | 'role' | 'group' | 'user';
  platform: 'web' | 'mobile' | 'email';
  settings: Record<string, any>;
  role?: 'Organization Admin' | 'Analyst' | 'Viewer';
  groupId?: number;
  userId?: number;
}) => {
  const res = await axiosInstance.patch(API_ROUTES.USER_SETTINGS_APPLY, data);
  return res.data;
};

export const getUserSettingsByUserId = async (userId: number) => {
  const res = await axiosInstance.get(`${API_ROUTES.USER_SETTINGS_USER}/${userId}`);
  return res.data;
};

export const getScopePolicySettings = async (params: {
  scope: 'org' | 'role' | 'group' | 'user';
  platform: 'web' | 'mobile' | 'email';
  role?: 'Organization Admin' | 'Analyst' | 'Viewer';
  groupId?: number;
  userId?: number;
}) => {
  const query = new URLSearchParams();
  query.set('scope', params.scope);
  query.set('platform', params.platform);
  if (params.role) query.set('role', params.role);
  if (typeof params.groupId === 'number') query.set('groupId', `${params.groupId}`);
  if (typeof params.userId === 'number') query.set('userId', `${params.userId}`);
  const res = await axiosInstance.get(`${API_ROUTES.USER_SETTINGS_POLICY}?${query.toString()}`);
  return res.data;
};

export const getPublicDownloadLinkExpiry = async () => {
  const res = await axiosInstance.get(API_ROUTES.USER_PUBLIC_DOWNLOAD_LINK_EXPIRY);
  return res.data;
};

export const patchPublicDownloadLinkExpiry = async (ttlSeconds: number) => {
  const res = await axiosInstance.patch(API_ROUTES.USER_PUBLIC_DOWNLOAD_LINK_EXPIRY, {
    ttlSeconds,
  });
  return res.data;
};

export const getOrganizationPublicDownloadLinkExpiry = async (organizationId: string) => {
  const res = await axiosInstance.get(
    `${API_ROUTES.USER_PUBLIC_DOWNLOAD_LINK_EXPIRY}/organization/${organizationId}`,
  );
  return res.data;
};

export const patchOrganizationPublicDownloadLinkExpiry = async (
  organizationId: string,
  ttlSeconds: number,
) => {
  const res = await axiosInstance.patch(
    `${API_ROUTES.USER_PUBLIC_DOWNLOAD_LINK_EXPIRY}/organization/${organizationId}`,
    { ttlSeconds },
  );
  return res.data;
};

export const getAllUsers = async (
  page: number | null = 1,
  limit: number | null = 10,
  search = '',
  sort = 'asc',
  role?: string | string[],
  sortField?: string,
) => {
  let pageQuery = typeof page === 'number' && typeof limit === 'number' ? `page=${page + 1}&limit=${limit}&` : '';

  let searchText = '';
  if (search?.toLocaleLowerCase().includes('+')) {
    searchText = search.replace(/\+/g, '%2B');
  } else if (search) {
    searchText = search;
  }

  if (role && Array.isArray(role)) {
    let rolesQuery = role.map((r) => `&role=${r}`).join('');
    return await axiosInstance.get(
      `${API_ROUTES.USER}?${pageQuery}search=${searchText}${sort ? `&sort=${sort}` : ''}${rolesQuery}${
        sortField ? `&sortField=${sortField}` : ''
      }`,
    );
  }
  let rolesQuery = `&role=${role}`;
  if (!role) {
    rolesQuery = '';
  }
  return await axiosInstance.get(
    `${API_ROUTES.USER}?${pageQuery}search=${searchText}&sort=${sort}${rolesQuery}${sortField ? `&sortField=${sortField}` : ''}`,
  );
};

export const getAllUsersByOrgId = async (
  orgId: string,
  page: number | null = 1,
  limit: number | null = 10,
  search = '',
  sort = 'asc',
  role?: string | string[],
  sortField?: string,
) => {
  let pageQuery = typeof page === 'number' && typeof limit === 'number' ? `page=${page + 1}&limit=${limit}&` : '';

  if (role && Array.isArray(role)) {
    let rolesQuery = role.map((r) => `&role=${r}`).join('');
    return await axiosInstance.get(
      `${API_ROUTES.USER}?org_id=${orgId}${pageQuery}search=${search}${sort ? `&sort=${sort}` : ''}${rolesQuery}${
        sortField ? `&sortField=${sortField}` : ''
      }`,
    );
  }
  let rolesQuery = `&role=${role}`;
  if (!role) {
    rolesQuery = '';
  }
  return await axiosInstance.get(
    `${API_ROUTES.USER}?org_id=${orgId}${pageQuery}search=${search}&sort=${sort}${rolesQuery}${sortField ? `&sortField=${sortField}` : ''}`,
  );
};

export const postCreateSuperAdmin = async (
  email: string,
  selectedOrgs: { organization_id: string; role: RolesLowerCase }[],
  username: string,
) => {
  const body: any = { email, username };
  if (selectedOrgs.length) {
    body.organizations = selectedOrgs;
  }
  const res = await axiosInstance.post(API_ROUTES.CREATE_SUPER_ADMIN, {
    ...body,
    email: email.toLocaleLowerCase(),
  });
  return res;
};

export const postUserHelpResponse = async (data: {
  userId: number;
  successfulResponse?: boolean;
  responseText?: string;
  instantFinish?: boolean;
}) => {
  return await axiosInstance.post(API_ROUTES.POST_USER_HELP_RESPONSE, data);
};

export const patchRevokeSuperAdmin = async (userId: string) => {
  return await axiosInstance.patch(`${API_ROUTES.REVOKE_SUPER_ADMIN}/${userId}`);
};

export const getUserPreviewToken = async (userId: string) => {
  return await axiosInstance.post(`${API_ROUTES.GET_PREVIEW_ACCESS_TOKEN}/${userId}`);
};

export const getUserById = async (userId: string) => {
  return await axiosInstance.get(`${API_ROUTES.USER}/${userId}`);
};

export const postResendRegistrationLink = async (data: { all?: boolean; userIds?: number[] }) => {
  return await axiosInstance.post(API_ROUTES.RESEND_REGISTRATION_EMAIL, data);
};

export const postRequestHelp = async (data: { admins: { id: string }[]; requestHelpMessage: string }) => {
  return await axiosInstance.post(API_ROUTES.POST_USER_REQUEST_HELP, data);
};

export const postCancelRequestHelp = async () => {
  return await axiosInstance.delete(API_ROUTES.POST_USER_REQUEST_HELP);
};

export const getUsersByEmailArray = async (emailArr: string[], organization_id: string) => {
  return await axiosInstance.post(`${API_ROUTES.GET_USERS_BY_EMAIL_ARRAY}?organization_id=${organization_id}`, {
    emails: emailArr,
  });
};
